const IS_DEV = process.env.NEXT_PUBLIC_TB_MODE === "test"
const API_BASE = typeof window === "undefined"
  ? (IS_DEV ? "http://127.0.0.1:182" : "http://127.0.0.1:282")
  : (IS_DEV ? "https://dev.maxiaohash.top" : "")

const TB_MODE = process.env.NEXT_PUBLIC_TB_MODE

function withTbMode(q: URLSearchParams) {
  if (TB_MODE) q.set("tb_mode", TB_MODE)
  return q
}

export interface ArticleItem {
  article_id: string
  title: string
  slug: string
  summary: string
  category: string[]
  tags: string[]
  keywords: string[]
  status: string
  published_at: string | null
  created_at: string
  updated_at: string
  display_created_at: string | null
  display_updated_at: string | null
  view_count: number
}

export interface ArticleDetail extends ArticleItem {
  content: string
}

export interface ArticleListResp {
  total: number
  page: number
  page_size: number
  items: ArticleItem[]
}

export async function fetchArticles(params?: {
  page?: number
  page_size?: number
  category?: string
  tag?: string
}): Promise<ArticleListResp> {
  const q = withTbMode(new URLSearchParams({
    page: String(params?.page ?? 1),
    page_size: String(params?.page_size ?? 20),
    status: "published",
    ...(params?.category ? { category: params.category } : {}),
    ...(params?.tag ? { tag: params.tag } : {}),
  }))
  const res = await fetch(`${API_BASE}/api/blog/articles?${q}`, { cache: "no-store" })
  if (!res.ok) throw new Error("fetch articles failed")
  return res.json()
}

export async function fetchArticle(articleId: string): Promise<ArticleDetail> {
  const q = withTbMode(new URLSearchParams())
  const res = await fetch(`${API_BASE}/api/blog/articles/${articleId}?${q}`, { cache: "no-store" })
  if (!res.ok) throw new Error("fetch article failed")
  return res.json()
}

// ---------- admin ----------

export interface LoginResponse {
  access_token: string
  refresh_token: string
  role: string
  expires_in: number
}

async function adminFetch(path: string, init: RequestInit = {}, token?: string): Promise<Response> {
  const q = withTbMode(new URLSearchParams())
  const sep = path.includes("?") ? "&" : "?"
  const url = `${API_BASE}${path}${q.toString() ? sep + q.toString() : ""}`
  const headers = new Headers(init.headers)
  headers.set("Content-Type", "application/json")
  if (token) headers.set("Authorization", `Bearer ${token}`)
  return fetch(url, { ...init, headers, cache: "no-store" })
}

export async function adminLogin(username: string, password: string): Promise<LoginResponse> {
  const res = await adminFetch("/api/blog_admin/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  })
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "登录失败")
  return res.json()
}

export async function adminLogout(token: string): Promise<void> {
  await adminFetch("/api/blog_admin/auth/logout", { method: "POST" }, token)
}

export async function adminListArticles(
  token: string,
  params?: { page?: number; page_size?: number; status?: string }
): Promise<ArticleListResp> {
  const sp = new URLSearchParams({
    page: String(params?.page ?? 1),
    page_size: String(params?.page_size ?? 20),
    ...(params?.status ? { status: params.status } : {}),
  })
  if (TB_MODE) sp.set("tb_mode", TB_MODE)
  const res = await fetch(`${API_BASE}/api/blog_admin/articles?${sp}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  })
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error("fetch admin articles failed")
  return res.json()
}

export async function adminDeleteArticle(token: string, articleId: string): Promise<void> {
  const res = await adminFetch(`/api/blog_admin/articles/${articleId}`, { method: "DELETE" }, token)
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error("delete failed")
}

export interface CreateArticleReq {
  title: string
  slug: string
  summary?: string
  content?: string
  category?: string[]
  tags?: string[]
  keywords?: string[]
  status?: string
  display_created_at?: string
}

export interface UpdateArticleReq extends Partial<CreateArticleReq> {
  change_note?: string
  display_updated_at?: string
}

export async function adminCreateArticle(token: string, body: CreateArticleReq): Promise<{ article_id: string }> {
  const res = await adminFetch("/api/blog_admin/articles", { method: "POST", body: JSON.stringify(body) }, token)
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "创建失败")
  return res.json()
}

export async function adminUpdateArticle(token: string, articleId: string, body: UpdateArticleReq): Promise<void> {
  const res = await adminFetch(`/api/blog_admin/articles/${articleId}`, { method: "PUT", body: JSON.stringify(body) }, token)
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "更新失败")
}

export async function adminGetArticle(token: string, articleId: string): Promise<ArticleDetail> {
  const res = await adminFetch(`/api/blog_admin/articles/${articleId}`, {}, token)
  if (!res.ok) throw new Error("load article failed")
  return res.json()
}

export interface AttachmentItem {
  attach_id: string
  article_id: string
  file_name: string
  file_size: number
  file_url: string
  created_at: string
}

export async function fetchAttachments(articleId: string): Promise<AttachmentItem[]> {
  const q = withTbMode(new URLSearchParams())
  const res = await fetch(`${API_BASE}/api/blog/articles/${articleId}/attachments${q.toString() ? "?" + q : ""}`, { cache: "no-store" })
  if (!res.ok) return []
  return res.json()
}

export async function adminUploadAttachment(token: string, articleId: string, file: File): Promise<AttachmentItem> {
  const q = withTbMode(new URLSearchParams())
  const sep = q.toString() ? "?" : ""
  const url = `${API_BASE}/api/blog_admin/articles/${articleId}/attachments${sep}${q}`
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form, cache: "no-store" })
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "上传失败")
  return res.json()
}

export async function adminDeleteAttachment(token: string, attachId: string): Promise<void> {
  const res = await adminFetch(`/api/blog_admin/attachments/${attachId}`, { method: "DELETE" }, token)
  if (res.status === 401) throw new Error("UNAUTHORIZED")
  if (!res.ok) throw new Error("删除失败")
}
